import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, Box } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Pages and Components
import HomePage from './pages/HomePage';
import UploadPage from './pages/UploadPage';
import ProductionsPage from './pages/ProductionsPage';
import AdDetailPage from './pages/AdDetailPage';
import JobsPage from './pages/JobsPage';
import AdsTablePage from './pages/AdsTablePage';
import LicensedSongsPage from './pages/LicensedSongsPage';
import LicensesPage from './pages/LicensesPage';
import LicenseDetailPage from './pages/LicenseDetailPage';
import { Organization } from './pages/Organization';
import Header from './components/common/Header';
import TaglineBanner from './components/common/TaglineBanner';
import ErrorBoundary from './components/common/ErrorBoundary';
import BuildInfo from './components/common/BuildInfo';

// Auth Components
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/auth/ProtectedRoute';

// Create Material-UI theme
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
      light: '#42a5f5',
      dark: '#1565c0',
    },
    secondary: {
      main: '#dc004e',
      light: '#ff5983',
      dark: '#9a0036',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
  },
  typography: {
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 500,
    },
    h6: {
      fontWeight: 500,
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          borderRadius: 12,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 500,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
          },
        },
      },
    },
  },
});

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 1,
    },
  },
});

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <ErrorBoundary>
          <AuthProvider>
            <Router>
              <Box sx={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: 'background.default'
              }}>
                <Header />
                <TaglineBanner />

                <Box component="main" sx={{
                  flexGrow: 1,
                  padding: { xs: 2, sm: 3 },
                  maxWidth: '1200px',
                  width: '100%',
                  mx: 'auto'
                }}>
                  <Routes>
                    {/* Homepage - Protected route */}
                    <Route
                      path="/"
                      element={
                        <ProtectedRoute requireHellosimplyDomain={true}>
                          <HomePage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Upload Page - Protected route requiring @hellosimply.com access */}
                    <Route
                      path="/upload"
                      element={
                        <ProtectedRoute requireHellosimplyDomain={true}>
                          <UploadPage />
                        </ProtectedRoute>
                      }
                    />
                    
                    {/* Productions Management - Protected routes */}
                    <Route 
                      path="/productions" 
                      element={
                        <ProtectedRoute requireHellosimplyDomain={true}>
                          <ProductionsPage />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/productions/:productionId" 
                      element={
                        <ProtectedRoute requireHellosimplyDomain={true}>
                          <ProductionsPage />
                        </ProtectedRoute>
                      } 
                    />
                    
                    {/* Ad Detail Page - Public route (conditional content based on auth) */}
                    <Route
                      path="/ads/:adId"
                      element={<AdDetailPage />}
                    />
                    
                    {/* Jobs Dashboard - Protected route */}
                    <Route 
                      path="/jobs" 
                      element={
                        <ProtectedRoute requireHellosimplyDomain={true}>
                          <JobsPage />
                        </ProtectedRoute>
                      } 
                    />
                    
                    {/* Ads Library - Protected route */}
                    <Route 
                      path="/ads" 
                      element={
                        <ProtectedRoute requireHellosimplyDomain={true}>
                          <AdsTablePage />
                        </ProtectedRoute>
                      } 
                    />
                    
                    {/* Licensed Songs Management (legacy) - Protected route */}
                    <Route 
                      path="/licensed-songs" 
                      element={
                        <ProtectedRoute requireHellosimplyDomain={true}>
                          <LicensedSongsPage />
                        </ProtectedRoute>
                      } 
                    />

                    {/* Licenses (new system) - Protected routes */}
                    <Route 
                      path="/licenses" 
                      element={
                        <ProtectedRoute requireHellosimplyDomain={true}>
                          <LicensesPage />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/licenses/:id" 
                      element={
                        <ProtectedRoute requireHellosimplyDomain={true}>
                          <LicenseDetailPage />
                        </ProtectedRoute>
                      } 
                    />
                    

                    
                    {/* Creative Organization - Protected route */}
                    <Route 
                      path="/organization" 
                      element={
                        <ProtectedRoute requireHellosimplyDomain={true}>
                          <Organization />
                        </ProtectedRoute>
                      } 
                    />

                    {/* Catch-all route */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </Box>
              </Box>
            </Router>
          </AuthProvider>
        </ErrorBoundary>
        <BuildInfo />
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App; 